import React, { useState, useEffect, useRef } from 'react';
import { AxiosInstanceWithCache } from '../../../components/utils';

interface AudienceExperience {
  id: string;
  caption: string;
  youtubeUrl: string;
  createdate: string;
}

const AudienceExperiences: React.FC = () => {
  const [videos, setVideos] = useState<AudienceExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAudienceExperiences();
  }, []);

  const fetchAudienceExperiences = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstanceWithCache.get('/api/getaudienceexperience');
      if (response.data.result === 'Success') {
        const data = response.data.data;
        // Convert object to array if needed
        const videoArray = Array.isArray(data) ? data : Object.values(data || {});
        setVideos(videoArray);
      }
    } catch (error) {
      console.error('Failed to fetch audience experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    if (videos.length <= 3) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      return nextIndex >= videos.length ? 0 : nextIndex;
    });
    
    // Reset transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (videos.length <= 3 || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => {
      const prev = prevIndex - 1;
      return prev < 0 ? videos.length - 1 : prev;
    });
    
    // Reset transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToSlide = (index: number) => {
    if (videos.length <= 3) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    // Reset transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Auto-advance carousel every 1 second
  useEffect(() => {
    if (videos.length <= 3) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [videos.length, currentIndex]);

  if (loading) {
    return (
      <section className="audience-experiences-section" aria-label="Loading audience experiences">
        <h3>Audience Experiences</h3>
        <div className="loading-spinner">Loading videos...</div>
      </section>
    );
  }

  // If we have 3 or fewer videos, show them all without carousel controls
  if (videos.length <= 3) {
    return (
      <section className="audience-experiences-section" aria-label="Audience experiences">
        <h3>Audience Experiences</h3>
        <div className="audience-carousel" role="list">
          {videos.map((video, index) => (
            <div key={video.id || index} className="audience-card" role="listitem" tabIndex={0} aria-label={`${video.caption} video`}>
              <div className="youtube-video-container">
                <iframe
                  width="100%"
                  height="166"
                  src={video.youtubeUrl}
                  title={`${video.caption} Video`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="audience-caption">{video.caption}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Get the 3 videos to display based on current index
  const getVisibleVideos = (): AudienceExperience[] => {
    const result: AudienceExperience[] = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % videos.length;
      result.push(videos[index]);
    }
    return result;
  };

  const visibleVideos = getVisibleVideos();

  return (
    <>
      <section className="audience-experiences-section" aria-label="Audience experiences video carousel">
        <h3>Audience Experiences</h3>
        <div className="audience-carousel-container">
          <button 
            className="carousel-arrow left-arrow" 
            onClick={prevSlide}
            aria-label="Previous video"
            disabled={isTransitioning}
          >
            ‹
          </button>
          
          <div className="audience-carousel" role="list" ref={carouselRef}>
            {visibleVideos.map((video, index) => (
              <div 
                key={video.id || `${currentIndex}-${index}`} 
                className="audience-card" 
                role="listitem" 
                tabIndex={0} 
                aria-label={`${video.caption} video`}
              >
                <div className="youtube-video-container">
                  <iframe
                    width="100%"
                    height="166"
                    src={video.youtubeUrl}
                    title={`${video.caption} Video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="audience-caption">{video.caption}</div>
              </div>
            ))}
          </div>
          
          <button 
            className="carousel-arrow right-arrow" 
            onClick={nextSlide}
            aria-label="Next video"
            disabled={isTransitioning}
          >
            ›
          </button>
        </div>
        
        {/* Carousel indicators */}
        <div className="carousel-indicators" role="tablist" aria-label="Video indicators">
          {videos.map((_, index) => (
            <button
              key={index}
              className={`indicator-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to video ${index + 1}`}
              aria-selected={index === currentIndex ? 'true' : 'false'}
              role="tab"
              tabIndex={0}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default AudienceExperiences;
